import {
  afterRenderEffect,
  AfterRenderRef,
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { EqualHeightGroupDirective } from './equal-height-group.directive';

@Directive({
  selector: '[uiEqualHeightItem]',
  host: {
    '[style.min-height.px]': 'minHeight()',
  },
})
export class EqualHeightItemDirective {
  public readonly group: InputSignal<EqualHeightGroupDirective> =
    input.required<EqualHeightGroupDirective>({ alias: 'uiEqualHeightItem' });

  public readonly equalHeightTarget: InputSignal<string | undefined> = input<string>();

  protected readonly minHeight: Signal<number | null> = computed(() => this.group().minHeight());

  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

  private currentGroup?: EqualHeightGroupDirective;
  private currentTarget?: HTMLElement;

  private readonly observeAfterRenderRef: AfterRenderRef = afterRenderEffect({
    read: onCleanup => {
      this.syncTarget();

      const host: HTMLElement = this.elementRef.nativeElement;

      if (typeof MutationObserver !== 'undefined') {
        const mutationObserver: MutationObserver = new MutationObserver(() => {
          this.syncTarget();
        });

        mutationObserver.observe(host, {
          childList: true,
          subtree: true,
        });

        onCleanup(() => {
          mutationObserver.disconnect();
        });
      }

      onCleanup(() => {
        this.unregisterCurrentTarget();
      });
    },
  });

  private syncTarget(): void {
    const group: EqualHeightGroupDirective = this.group();
    const host: HTMLElement = this.elementRef.nativeElement;
    const selector: string | undefined = this.equalHeightTarget();
    const target: HTMLElement | null = selector
      ? host.querySelector<HTMLElement>(selector)
      : host;

    if (this.currentGroup === group && this.currentTarget === target) {
      return;
    }

    this.unregisterCurrentTarget();

    if (!target) {
      return;
    }

    this.currentGroup = group;
    this.currentTarget = target;
    group.register(target);
  }

  private unregisterCurrentTarget(): void {
    if (this.currentGroup && this.currentTarget) {
      this.currentGroup.unregister(this.currentTarget);
    }

    this.currentGroup = undefined;
    this.currentTarget = undefined;
  }
}
